import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Briefcase, Users, FileText, Plus, Pencil, Trash2, X, Search } from "lucide-react";

export const INVOICE_STATUS = {
  brouillon: "Brouillon",
  envoyee: "Envoyée",
  payee: "Payée",
  annulee: "Annulée",
};
export const INVOICE_STYLES = {
  brouillon: "bg-slate-100 text-slate-600",
  envoyee: "bg-blue-50 text-blue-700",
  payee: "bg-green-50 text-green-700",
  annulee: "bg-red-50 text-red-600",
};
const KIND_LABELS = { client: "Client", prospect: "Prospect", fournisseur: "Fournisseur" };

export const LinesEditor = ({ lines, onChange }) => {
  const update = (i, field, value) => {
    const next = lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l));
    onChange(next);
  };
  return (
    <div data-testid="lines-editor" className="space-y-2">
      <div className="grid grid-cols-[1fr_70px_100px_90px_32px] gap-2 text-xs font-medium text-slate-500 px-1">
        <span>Description</span><span className="text-right">Qté</span>
        <span className="text-right">Prix unit.</span><span className="text-right">Montant</span><span />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-[1fr_70px_100px_90px_32px] gap-2 items-center">
          <input
            data-testid={`line-description-${i}`}
            value={l.description}
            onChange={(e) => update(i, "description", e.target.value)}
            placeholder="Prestation ou produit…"
            className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
          />
          <input
            data-testid={`line-quantity-${i}`}
            type="number" min="0.01" step="0.01" value={l.quantity}
            onChange={(e) => update(i, "quantity", e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
          />
          <input
            data-testid={`line-price-${i}`}
            type="number" min="0" step="0.01" value={l.unit_price}
            onChange={(e) => update(i, "unit_price", e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
          />
          <span className="text-sm text-right text-slate-600 pr-1">
            {((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)).toFixed(2)} $
          </span>
          <button
            data-testid={`line-remove-${i}`}
            onClick={() => onChange(lines.filter((_, idx) => idx !== i))}
            disabled={lines.length <= 1}
            aria-label="Retirer la ligne"
            className="text-slate-400 hover:text-red-600 disabled:opacity-30"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        data-testid="line-add-button"
        onClick={() => onChange([...lines, { description: "", quantity: 1, unit_price: "" }])}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-brand-navy"
      >
        <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
      </button>
    </div>
  );
};

export const computeTotals = (lines, applyTps, applyTvq) => {
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const tps = applyTps ? subtotal * 0.05 : 0;
  const tvq = applyTvq ? subtotal * 0.09975 : 0;
  return { subtotal, tps, tvq, total: subtotal + tps + tvq };
};

export const InvoiceFormDialog = ({ contacts, initial, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.invoice_id);
  const [contactId, setContactId] = useState(initial?.contact_id || "");
  const [clientName, setClientName] = useState(initial?.client_name || "");
  const [clientEmail, setClientEmail] = useState(initial?.client_email || "");
  const [dueDate, setDueDate] = useState(initial?.due_date || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [applyTps, setApplyTps] = useState(initial ? initial.apply_tps : true);
  const [applyTvq, setApplyTvq] = useState(initial ? initial.apply_tvq : true);
  const [lines, setLines] = useState(
    initial?.lines?.map((l) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price })) ||
      [{ description: "", quantity: 1, unit_price: "" }]
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const totals = computeTotals(lines, applyTps, applyTvq);

  const pickContact = (cid) => {
    setContactId(cid);
    const c = contacts.find((x) => x.contact_id === cid);
    if (c) {
      setClientName(c.company_name || c.name);
      setClientEmail(c.email || "");
    }
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        contact_id: contactId || null,
        client_name: clientName,
        client_email: clientEmail,
        due_date: dueDate || null,
        notes,
        apply_tps: applyTps,
        apply_tvq: applyTvq,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 0,
          unit_price: parseFloat(l.unit_price) || 0,
        })),
      };
      const res = isEdit
        ? await api.put(`/business/invoices/${initial.invoice_id}`, payload)
        : await api.post("/business/invoices", payload);
      onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="invoice-form-dialog" className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">{isEdit ? `Modifier ${initial.number}` : "Nouvelle facture"}</h2>
          <button data-testid="invoice-form-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="invoice-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-brand-navy">Contact CRM (optionnel)</label>
              <select
                data-testid="invoice-contact-select"
                value={contactId}
                onChange={(e) => pickContact(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              >
                <option value="">— Saisie libre —</option>
                {contacts.map((c) => (
                  <option key={c.contact_id} value={c.contact_id}>{c.name}{c.company_name ? ` (${c.company_name})` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-brand-navy">Date d'échéance</label>
              <input
                data-testid="invoice-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-brand-navy">Client (nom ou entreprise)</label>
              <input
                data-testid="invoice-client-name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-navy">Courriel du client</label>
              <input
                data-testid="invoice-client-email"
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy block mb-1">Lignes de facturation</label>
            <LinesEditor lines={lines} onChange={setLines} />
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="inline-flex items-center gap-2 text-sm text-brand-navy">
              <input data-testid="invoice-apply-tps" type="checkbox" checked={applyTps} onChange={(e) => setApplyTps(e.target.checked)} className="rounded" />
              TPS (5 %)
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-brand-navy">
              <input data-testid="invoice-apply-tvq" type="checkbox" checked={applyTvq} onChange={(e) => setApplyTvq(e.target.checked)} className="rounded" />
              TVQ (9,975 %)
            </label>
          </div>
          <div data-testid="invoice-totals-preview" className="bg-slate-50 rounded-lg px-4 py-3 text-sm space-y-1">
            <div className="flex justify-between text-slate-600"><span>Sous-total</span><span>{totals.subtotal.toFixed(2)} $</span></div>
            {applyTps && <div className="flex justify-between text-slate-600"><span>TPS (5 %)</span><span>{totals.tps.toFixed(2)} $</span></div>}
            {applyTvq && <div className="flex justify-between text-slate-600"><span>TVQ (9,975 %)</span><span>{totals.tvq.toFixed(2)} $</span></div>}
            <div className="flex justify-between font-bold text-brand-navy"><span>Total CAD</span><span>{totals.total.toFixed(2)} $</span></div>
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Notes (visibles sur la facture)</label>
            <textarea
              data-testid="invoice-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <button
            data-testid="invoice-save-button"
            onClick={save}
            disabled={saving}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer la facture (brouillon)"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactFormDialog = ({ initial, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.contact_id);
  const [name, setName] = useState(initial?.name || "");
  const [kind, setKind] = useState(initial?.kind || "client");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [companyName, setCompanyName] = useState(initial?.company_name || "");
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [notes, setNotes] = useState(initial?.notes || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        name, kind, email, phone, company_name: companyName, notes,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const res = isEdit
        ? await api.put(`/business/contacts/${initial.contact_id}`, payload)
        : await api.post("/business/contacts", payload);
      onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="contact-form-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">{isEdit ? "Modifier le contact" : "Nouveau contact"}</h2>
          <button data-testid="contact-form-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="contact-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-brand-navy">Nom</label>
              <input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-navy">Type</label>
              <select data-testid="contact-kind-select" value={kind} onChange={(e) => setKind(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise">
                <option value="client">Client</option>
                <option value="prospect">Prospect</option>
                <option value="fournisseur">Fournisseur</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-brand-navy">Courriel</label>
              <input data-testid="contact-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
            </div>
            <div>
              <label className="text-sm font-medium text-brand-navy">Téléphone</label>
              <input data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Entreprise</label>
            <input data-testid="contact-company-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Tags (séparés par des virgules)</label>
            <input data-testid="contact-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="vip, montréal…"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Notes</label>
            <textarea data-testid="contact-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise" />
          </div>
          <button data-testid="contact-save-button" onClick={save} disabled={saving}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50">
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Ajouter le contact"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Business() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "contacts";

  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [contactDialog, setContactDialog] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (kind) params.kind = kind;
      const res = await api.get("/business/contacts", { params });
      setContacts(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, kind]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (status) params.status = status;
      const res = await api.get("/business/invoices", { params });
      setInvoices(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (!user) return;
    if (tab === "contacts") loadContacts();
    else loadInvoices();
  }, [tab, user, loadContacts, loadInvoices]);

  const removeContact = async (contact) => {
    setError("");
    try {
      await api.delete(`/business/contacts/${contact.contact_id}`);
      loadContacts();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user) return null;

  const spinner = (
    <div className="flex justify-center py-16">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="business-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <Briefcase className="w-7 h-7 text-brand-turquoise" />
              NEXA Business
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Votre espace d'affaires : carnet de contacts (CRM) et facturation CAD avec TPS/TVQ.
            </p>
          </div>
          {tab === "contacts" ? (
            <button
              data-testid="create-contact-button"
              onClick={() => setContactDialog({})}
              className="inline-flex items-center gap-2 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
            >
              <Plus className="w-4 h-4" /> Nouveau contact
            </button>
          ) : (
            <button
              data-testid="create-invoice-button"
              onClick={() => setInvoiceDialog({})}
              className="inline-flex items-center gap-2 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
            >
              <Plus className="w-4 h-4" /> Nouvelle facture
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
          {[
            { key: "contacts", label: "Contacts", icon: Users },
            { key: "factures", label: "Factures", icon: FileText },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`business-tab-${t.key}`}
              onClick={() => setSearchParams(t.key === "contacts" ? {} : { tab: t.key })}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-brand-turquoise text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="business-error" className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {tab === "contacts" && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="contact-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un contact, une entreprise…"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
                />
              </div>
              <select
                data-testid="contact-kind-filter"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              >
                <option value="">Tous les types</option>
                <option value="client">Clients</option>
                <option value="prospect">Prospects</option>
                <option value="fournisseur">Fournisseurs</option>
              </select>
            </div>
            {loading ? spinner : contacts.length === 0 ? (
              <div data-testid="contacts-empty" className="text-center py-16 text-slate-400">
                Aucun contact. Ajoutez votre premier client, prospect ou fournisseur !
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {contacts.map((c) => (
                  <div key={c.contact_id} data-testid={`contact-row-${c.contact_id}`} className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-50">
                    <div className="w-9 h-9 rounded-full bg-brand-turquoise/15 flex items-center justify-center text-brand-turquoise font-bold text-sm shrink-0">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-medium text-brand-navy text-sm">
                        {c.name}
                        {c.company_name && <span className="text-slate-400 font-normal"> · {c.company_name}</span>}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                        {c.tags?.length > 0 && ` · ${c.tags.map((t) => `#${t}`).join(" ")}`}
                      </p>
                    </div>
                    <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                      c.kind === "client" ? "bg-green-50 text-green-700"
                      : c.kind === "prospect" ? "bg-amber-50 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>
                      {KIND_LABELS[c.kind]}
                    </span>
                    <button
                      data-testid={`edit-contact-${c.contact_id}`}
                      onClick={() => setContactDialog(c)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-100"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Modifier
                    </button>
                    <button
                      data-testid={`delete-contact-${c.contact_id}`}
                      onClick={() => removeContact(c)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "factures" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <select
                data-testid="invoice-status-filter"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
              >
                <option value="">Tous les statuts</option>
                <option value="brouillon">Brouillons</option>
                <option value="envoyee">Envoyées</option>
                <option value="payee">Payées</option>
                <option value="annulee">Annulées</option>
              </select>
            </div>
            {loading ? spinner : invoices.length === 0 ? (
              <div data-testid="invoices-empty" className="text-center py-16 text-slate-400">
                Aucune facture. Créez votre première facture en CAD !
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {invoices.map((inv) => (
                  <Link
                    key={inv.invoice_id}
                    data-testid={`invoice-row-${inv.invoice_id}`}
                    to={`/business/factures/${inv.invoice_id}`}
                    className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-medium text-brand-navy text-sm">{inv.number} — {inv.client_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString("fr-CA") : ""}
                        {inv.due_date && ` · échéance ${inv.due_date}`}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-brand-navy">{inv.total} $ {inv.currency}</span>
                    <span data-testid={`invoice-status-${inv.invoice_id}`} className={`text-xs font-medium rounded-full px-2.5 py-1 ${INVOICE_STYLES[inv.status]}`}>
                      {INVOICE_STATUS[inv.status]}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {contactDialog !== null && (
        <ContactFormDialog
          initial={contactDialog.contact_id ? contactDialog : null}
          onClose={() => setContactDialog(null)}
          onSaved={() => { setContactDialog(null); loadContacts(); }}
        />
      )}
      {invoiceDialog !== null && (
        <InvoiceFormDialog
          contacts={contacts}
          initial={null}
          onClose={() => setInvoiceDialog(null)}
          onSaved={(inv) => { setInvoiceDialog(null); navigate(`/business/factures/${inv.invoice_id}`); }}
        />
      )}
    </div>
  );
}
